import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import { PDFDownloadLink } from '@react-pdf/renderer'
import moment from 'moment'
import API from '../../../services/API'
import InfoCard from '../../../components/Cards/InfoCard'
import PTSRiskAssessmentPDF from './RiskAssessmentPDF'

import RiskAssessmentDialog, {
  RiskChip,
} from './RiskAssessmentDialog'

const RiskAssessments = ({ patient, orgName, orgLogo }) => {
  const [assessments, setAssessments] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedAssessment, setSelectedAssessment] = useState(null)
  const [saving, setSaving] = useState(false)

  const loadAssessments = async () => {
    if (!patient?._id) return
    setLoading(true)
    setError(null)
    try {
      const res = await API.get(`/pts/patient/${patient._id}/risk-assessments`)
      if (res.status === 200) setAssessments(res.data || [])
    } catch (e) {
      console.error(e)
      setError('Unable to load risk assessments.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadAssessments()
  }, [patient?._id])

  const latest = useMemo(() => assessments[0], [assessments])

  const saveAssessment = async (assessment) => {
    setSaving(true)
    try {
      const res = selectedAssessment?._id
        ? await API.put(
            `/pts/risk-assessments/${selectedAssessment._id}`,
            assessment,
          )
        : await API.post(
            `/pts/patient/${patient._id}/risk-assessments`,
            assessment,
          )

      if ([200, 201].includes(res.status)) {
        setDialogOpen(false)
        setSelectedAssessment(null)
        await loadAssessments()
      }
    } catch (e) {
      console.error(e)
      setError('Unable to save risk assessment.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <InfoCard>
      <Stack spacing={1.5}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box>
            <Typography variant="title2" fontWeight={600}>
              Risk Assessments
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Home visit, discharge safety, and transport planning assessments.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setSelectedAssessment(null)
              setDialogOpen(true)
            }}
          >
            New
          </Button>
        </Stack>
        <Divider />
        {error && <Alert severity="error">{error}</Alert>}
        {loading ? (
          <Typography color="text.secondary">
            Loading risk assessments...
          </Typography>
        ) : assessments.length ? (
          <>
            {latest && (
              <Box
                sx={{ p: 1.5, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0' }}
              >
                <Stack direction="row" justifyContent="space-between" gap={2}>
                  <Box>
                    <Typography fontWeight={950}>Latest Assessment</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {latest.assessmentType} |{' '}
                      {latest.visitDate
                        ? moment(latest.visitDate).format('DD/MM/YYYY HH:mm')
                        : moment(latest.createdAt).format('DD/MM/YYYY HH:mm')}
                    </Typography>
                  </Box>
                  <RiskChip risk={latest.overallRisk} />
                </Stack>
              </Box>
            )}
            {assessments.map((assessment) => (
              <Stack
                key={assessment._id}
                direction={{ xs: 'column', md: 'row' }}
                alignItems={{ xs: 'flex-start', md: 'center' }}
                justifyContent="space-between"
                spacing={1.5}
                sx={{ borderBottom: '1px solid #EEF2F7', pb: 1 }}
              >
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography fontWeight={900}>
                      {assessment.assessmentType}
                    </Typography>
                    <RiskChip risk={assessment.overallRisk} />
                    {assessment.hospitalVisible && (
                      <Chip size="small" label="Hospital Visible" />
                    )}
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {assessment.visitDate
                      ? moment(assessment.visitDate).format('DD/MM/YYYY HH:mm')
                      : moment(assessment.createdAt).format('DD/MM/YYYY HH:mm')}
                    {' | '}
                    {assessment.completedByName || 'Unknown assessor'}
                  </Typography>
                  {assessment.lastUpdatedByName && (
                    <Typography variant="caption" color="text.secondary">
                      Last updated by {assessment.lastUpdatedByName}
                      {' - '}
                      {moment(assessment.updatedAt).format('DD/MM/YYYY HH:mm')}
                    </Typography>
                  )}
                </Box>
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title="Edit assessment">
                    <IconButton
                      onClick={() => {
                        setSelectedAssessment(assessment)
                        setDialogOpen(true)
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <PDFDownloadLink
                    document={
                      <PTSRiskAssessmentPDF
                        assessment={assessment}
                        patient={patient}
                        orgName={orgName}
                        orgLogo={orgLogo}
                      />
                    }
                    fileName={`risk-assessment-${patient.patientID || patient._id}.pdf`}
                    style={{ textDecoration: 'none' }}
                  >
                    {({ loading: pdfLoading }) => (
                      <Tooltip title="Download PDF">
                        <span>
                          <IconButton disabled={pdfLoading}>
                            <PictureAsPdfIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </PDFDownloadLink>
                </Stack>
              </Stack>
            ))}
          </>
        ) : (
          <Alert severity="info">
            No risk assessments have been completed for this patient yet.
          </Alert>
        )}
      </Stack>
      <RiskAssessmentDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false)
          setSelectedAssessment(null)
        }}
        onSave={saveAssessment}
        patient={patient}
        selectedAssessment={selectedAssessment}
        saving={saving}
      />
    </InfoCard>
  )
}

export default RiskAssessments
